import React, { useCallback, useMemo, useRef, useEffect, useState, useContext } from 'react';

import { Card, ScrollableContainer, SearchInputToggle } from '@ergeon/core-components';
import { UsersIcon } from '@ergeon/icons/hero/micro';

import { Customer } from '../../../context-providers/CustomerContext';
import { StaffFilterContext } from '../../../context-providers/StaffFilterContext';
import { StaffFilter, useStaffFilterParams } from '../../StaffFilter';
import { usePagination } from '../../../hooks/usePagination';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useUnifiedDialog } from '../../../hooks/useUnifiedDialog';
import { useCentrifugoNotifications } from '../../../hooks/useCentrifugoNotifications';
import { useKeyboardShortcuts } from '../../../hooks/useKeyboardShortcuts';
import {
  getCustomerActiveOnly,
  setCustomerActiveOnly as saveCustomerActiveOnly,
} from '../../../utils/appStorage';
import { ROUTES } from '../../../routes';
import LoadingState from '../../LoadingState';
import { NoCustomersEmptyState } from '../../EmptyStates';
import { useCustomerActivitiesCounts } from '../hooks/useCustomerActivitiesCounts';
import { useCustomerSearch } from '../hooks/useCustomerSearch';
import { usePreservedSearchParams } from '../../../hooks/usePreservedSearchParams';

import CustomerItem from './CustomerItem';

export const CustomersPageContent: React.FC = () => {
  const [searchParams, setSearchParams] = usePreservedSearchParams();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { customerActivitiesCounts } = useCustomerActivitiesCounts();
  const { addUnread, getIncrementalCount, setTotalUnreadCount, dialogueIncrements } = useUnifiedDialog();
  const { selection, isSelectionInitialized } = useContext(StaffFilterContext);

  // Get API params from unified staff filter selection
  const { my_customers, staff_id } = useStaffFilterParams(selection);

  // Priority: URL > localStorage > default
  const urlActiveOnly = searchParams.get('activeOnly');
  const storedActiveOnly = getCustomerActiveOnly();
  let initialActiveOnly = storedActiveOnly;
  if (urlActiveOnly === 'true') {
    initialActiveOnly = true;
  } else if (urlActiveOnly === 'false') {
    initialActiveOnly = false;
  }

  const [isActiveOnly, setIsActiveOnlyState] = useState(initialActiveOnly);

  // Priority: URL > default (always start empty unless URL has search param)
  const urlSearch = searchParams.get('search');
  const initialSearch = urlSearch ?? '';

  // Get search functionality from hook
  const {
    searchQuery,
    setSearchQuery: setSearchQueryFromHook,
    fetchCustomersPage,
    flushSearch,
  } = useCustomerSearch(isActiveOnly, initialSearch, staff_id, my_customers);

  // Subscribe to real-time notifications for ALL customers
  const handleDialogueUpdated = useCallback(
    (notifiedCustomerId: string) => {
      const id = parseInt(notifiedCustomerId, 10);
      addUnread(id, 'dialogue');
    },
    [addUnread],
  );

  const handleActivitiesUpdated = useCallback(
    (notifiedCustomerId: string) => {
      const id = parseInt(notifiedCustomerId, 10);
      addUnread(id, 'activities');
    },
    [addUnread],
  );

  useCentrifugoNotifications({
    onDialogueUpdated: handleDialogueUpdated,
    onActivitiesUpdated: handleActivitiesUpdated,
  });

  // Update URL and localStorage when activeOnly changes
  const setIsActiveOnly = useCallback(
    (newValue: boolean) => {
      setIsActiveOnlyState(newValue);
      saveCustomerActiveOnly(newValue);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (newValue) {
          params.set('activeOnly', 'true');
        } else {
          params.delete('activeOnly');
        }
        return params;
      });
    },
    [setSearchParams],
  );

  // Wrap setSearchQuery to also update URL (but NOT localStorage - always start empty)
  const setSearchQuery = useCallback(
    (query: string) => {
      setSearchQueryFromHook(query);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        if (query) {
          params.set('search', query);
        } else {
          params.delete('search');
        }
        return params;
      });
    },
    [setSearchQueryFromHook, setSearchParams],
  );

  // Handle Enter key press in search input - immediately execute search
  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        flushSearch(); // Bypass debounce and search immediately
      }
    },
    [flushSearch],
  );

  // Set missing URL params from localStorage on mount (except search - always empty)
  useEffect(() => {
    const urlActiveOnly = searchParams.get('activeOnly');

    if (!urlActiveOnly && getCustomerActiveOnly()) {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set('activeOnly', 'true');
          return params;
        },
        { replace: true },
      );
    }
  }, []);

  // Sync state with URL on mount and URL changes
  useEffect(() => {
    const urlActiveOnly = searchParams.get('activeOnly');
    const urlSearch = searchParams.get('search');

    // Sync activeOnly
    const newActiveOnly = urlActiveOnly === 'true';
    if (newActiveOnly !== isActiveOnly) {
      setIsActiveOnlyState(newActiveOnly);
      saveCustomerActiveOnly(newActiveOnly);
    }

    // Sync search query from URL with hook's internal state (but NOT localStorage - always start empty)
    const targetSearch = urlSearch ?? '';
    if (targetSearch !== searchQuery) {
      setSearchQueryFromHook(targetSearch);
    }
  }, [searchParams]);

  // Register keyboard shortcut for search (Cmd/Ctrl + K)
  const isMac = useMemo(() => navigator.platform.toUpperCase().indexOf('MAC') >= 0, []);
  useKeyboardShortcuts({
    id: 'customer-search',
    shortcuts: [
      {
        key: 'k',
        [isMac ? 'metaKey' : 'ctrlKey']: true,
        description: 'Focus search input',
        category: 'search',
        allowInInput: true, // Allow this shortcut even when another input is focused
        action: () => {
          // Find the input element inside the search container and focus it
          const inputElement = searchContainerRef.current?.querySelector('input');
          if (inputElement) {
            inputElement.focus();
            inputElement.select(); // Select all text for easy replacement
          }
        },
      },
    ],
  });

  const pagination = usePagination<Customer>({
    fetchPage: fetchCustomersPage,
    initialPage: 1,
    enabled: isSelectionInitialized,
  });

  const { observerTarget } = useInfiniteScroll({
    hasMore: pagination.hasMore,
    isLoading: pagination.isLoading,
    onLoadMore: pagination.loadMore,
  });

  // Server API already returns customers sorted by unread status and modified_at
  const customers = pagination.data;

  const isLoadingData = pagination.isLoading || !isSelectionInitialized;
  const showEmptyState = !isLoadingData && customers.length === 0;

  // Calculate total unread count from all customers
  const calculatedTotalUnread = useMemo(() => {
    return customers.reduce((sum, customer) => {
      const incrementalUnread = getIncrementalCount(customer.id);
      const totalUnread = (customer.ud_unread_count || 0) + incrementalUnread;
      return sum + totalUnread;
    }, 0);
  }, [customers, getIncrementalCount, dialogueIncrements]);

  // Update context only when we have customers loaded (prevents badge from flickering)
  useEffect(() => {
    if (customers.length > 0) {
      setTotalUnreadCount(calculatedTotalUnread);
    }
  }, [calculatedTotalUnread, setTotalUnreadCount, customers.length]);

  const renderContent = () => {
    if (isLoadingData && customers.length === 0) {
      return <LoadingState />;
    }

    if (showEmptyState) {
      return <NoCustomersEmptyState />;
    }

    return (
      <div className="flex flex-col gap-2 py-1">
        {customers.map((customer) => {
          const activitiesData = customerActivitiesCounts.get(customer.id);
          const activitiesCount = activitiesData?.count || 0;
          const hasOverdueActivities = activitiesData?.hasOverdue || false;
          const href = ROUTES.customer.$buildPath({
            params: {
              customerId: customer.id,
            },
          });
          return (
            <CustomerItem
              activitiesCount={activitiesCount}
              customer={customer}
              hasOverdueActivities={hasOverdueActivities}
              href={href}
              incrementalUnread={getIncrementalCount(customer.id)}
              key={customer.id} />
          );
        })}
        {pagination.hasMore && <div className="h-0" ref={observerTarget} />}
        {pagination.hasMore && pagination.isLoading && <LoadingState />}
      </div>
    );
  };

  return (
    <Card className="flex flex-col gap-3 p-4 w-full h-full overflow-hidden" flavor="base" zIndex="1">
      <div className="flex justify-between items-center gap-4 min-h-8">
        <StaffFilter />

        <div className="flex items-center gap-4">
          <p className="text-gray-500 dark:text-gray-600 paragraph-3">
            {customers.length} {customers.length === 1 ? 'Customer' : 'Customers'} Shown
          </p>
          <div ref={searchContainerRef}>
            <SearchInputToggle
              isLoading={pagination.isLoading}
              isToggleEnabled={isActiveOnly}
              onChange={(value) => setSearchQuery(value || '')}
              onKeyDown={handleSearchKeyDown}
              onSubmit={(e) => e.preventDefault()}
              onToggleClick={setIsActiveOnly}
              placeholder="Search..."
              showSubmitButton={false}
              showToggle
              toggleIcon={<UsersIcon />}
              tooltipMsg="Active Customers Only"
              value={searchQuery} />
          </div>
        </div>
      </div>
      <ScrollableContainer className="px-2.5" direction="col" maskSize={0}>
        {renderContent()}
      </ScrollableContainer>
    </Card>
  );
};
